<template>
  <el-dialog
    :title="editMode && editMode.active ? hasCreatePermission ? `Edit Batch` : `View Batch` : 'New Batch'"
    size="small"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :visible="visible">
    <el-row type="flex" v-loading.body="loadingData" element-loading-text="Loading form..." style="min-height: 100px;">
      <el-col>
        <NewEntityForm
          :modalOpen="visible"
          :fields="formFields"
          :form.sync="form"
          :idPrefix="'f-'"
          ref="form"
          :nonAsyncSuggestions="nonAsyncSuggestions"
          @formChanged="formChanged"
        />
      </el-col>
    </el-row>
    <span slot="footer" class="dialog-footer">
      <el-button :disabled="saving" @click="changeVisibility(false)">
        Cancel
      </el-button>
      <el-button :disabled="!hasCreatePermission" :loading="saving" type="primary" @click="submitForm">
        Submit
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
  import NewEntityForm from '@/components/NewEntityForm'
  import { mapGetters } from 'vuex'
  import { notify } from '@/utils/helpers/Notify'
  import { validate } from '@/utils/helpers/Validate'
  import { payload } from '@/components/RouteComponents/Batches/actionPayload'
  import { systemViewMixins } from '@/Mixins/SystemViewMixins'
  import { DataLoader } from '@/utils/helpers/DataLoader/DataLoader'
  import { payload as bankActionPayload } from '@/components/RouteComponents/Banks/actionPayload'

  export default {
    components: { NewEntityForm },
    mixins: [ systemViewMixins ],
    props: {
      editMode: {
        'default': () => {
          return Object.assign({}, { active: false, modelId: null })
        },
        'type': Object
      },
      hasCreatePermission: Boolean
    },
    data () {
      return {
        modalName: 'batch',
        formFields: [
          {
            label: 'Name',
            name: 'name',
            dataType: 'string',
            fieldType: 'input',
            required: true,
            disabled: !this.hasCreatePermission,
            msg: 'Please specify a name'
          },
          {
            label: 'Active',
            name: 'active',
            dataType: 'boolean',
            fieldType: 'checkbox',
            disabled: !this.hasCreatePermission
          },
          {
            label: 'Description',
            name: 'description',
            dataType: 'string',
            fieldType: 'input',
            disabled: !this.hasCreatePermission
          },
          {
            label: 'Bank',
            name: 'bank',
            dataType: 'string',
            fieldType: 'select',
            filterable: true,
            required: true,
            disabled: !this.hasCreatePermission
          },
          {
            label: 'Production',
            name: 'production',
            fieldType: 'select',
            filterable: true,
            required: true,
            disabled: !this.hasCreatePermission
          },
          {
            label: 'Company',
            name: 'company',
            fieldType: 'select',
            filterable: true,
            required: true,
            disabled: !this.hasCreatePermission
          },
          {
            label: 'Default Series Code',
            name: 'default_series',
            fieldType: 'select',
            filterable: true,
            required: false,
            disabled: !this.hasCreatePermission
          },
          {
            label: 'Default Location Code',
            name: 'default_location',
            fieldType: 'select',
            filterable: true,
            required: false,
            disabled: !this.hasCreatePermission
          },
          {
            label: 'Default Set Code',
            name: 'default_set',
            fieldType: 'select',
            filterable: true,
            required: false,
            disabled: !this.hasCreatePermission
          },
          {
            label: 'Default Tax Code',
            name: 'default_tax_code',
            fieldType: 'select',
            filterable: true,
            required: false,
            disabled: !this.hasCreatePermission
          },
          {
            label: 'Default FF1 Code',
            name: 'default_free_field_1',
            fieldType: 'select',
            filterable: true,
            required: false,
            disabled: !this.hasCreatePermission
          },
          {
            label: 'Default FF2 Code',
            name: 'default_free_field_2',
            fieldType: 'select',
            filterable: true,
            required: false,
            disabled: !this.hasCreatePermission
          },
          {
            label: 'Default Ins Code',
            name: 'default_insurance',
            fieldType: 'select',
            filterable: true,
            required: false,
            disabled: !this.hasCreatePermission
          }
        ],
        form: {
          name: '',
          active: true,
          description: '',
          bank: { label: '', value: '' },
          production: { label: '', value: '' },
          company: { label: '', value: '' },
          default_series: { label: '', value: '' },
          default_location: { label: '', value: '' },
          default_set: { label: '', value: '' },
          default_free_field_1: { label: '', value: '' },
          default_free_field_2: { label: '', value: '' },
          default_tax_code: { label: '', value: '' },
          default_insurance: { label: '', value: '' }
        },
        formInitState: {
          name: '',
          active: true,
          description: '',
          bank: { label: '', value: '' },
          production: { label: '', value: '' },
          company: { label: '', value: '' },
          default_series: { label: '', value: '' },
          default_location: { label: '', value: '' },
          default_set: { label: '', value: '' },
          default_free_field_1: { label: '', value: '' },
          default_free_field_2: { label: '', value: '' },
          default_tax_code: { label: '', value: '' },
          default_insurance: { label: '', value: '' }
        },
        nonAsyncSuggestions: {
          company: [],
          production: [],
          bank: [],
          default_series: [],
          default_location: [],
          default_set: [],
          default_free_field_1: [],
          default_free_field_2: [],
          default_tax_code: [],
          default_insurance: []
        },
        createdNotif: null
      }
    },
    methods: {
      /**
       * Governs visibility of modal
       */
      changeVisibility (visible) {
        if (!visible) {
          this.clearForm()
          this.$emit('disableEditMode')
        }
        if (!this.inDataEntryView) {
          this.$store.dispatch('isEditingForm', false)
        }
        this.$store.dispatch('changeModalVisibility', { entityType: 'batch', visible, inDataEntryView: this.inDataEntryView })
      },
      /**
       * Validates form data and submits if pass, show err if fail
       */
      async submitForm () {
        const form = Object.assign({}, this.form)
        let validationErrors = validate(form, this.formFields)
        if (!validationErrors.messages) {
          form.bank = this.form.bank.value
          form.production = this.form.production.value
          form.company = this.form.company.value
          form.default_series = this.form.default_series.value
          form.default_location = this.form.default_location.value
          form.default_set = this.form.default_set.value
          form.default_free_field_1 = this.form.default_free_field_1.value
          form.default_free_field_2 = this.form.default_free_field_2.value
          form.default_tax_code = this.form.default_tax_code.value
          form.default_insurance = this.form.default_insurance.value
          let newPayload = Object.assign({}, payload, { id: this.editMode.modelId, data: form })
          try {
            const resp = await this.$store.dispatch(this.editMode.active ? 'update' : 'save', newPayload)
            if (!(resp && resp.id)) {
              throw new Error('Error saving batch')
            }
            this.changeVisibility(false)
            if (this.inDataEntryView) { // If in a data entry view, populate fields
              this.$emit('populateFieldWithNewModel', { type: 'batch', getter: 'getBatchById', id: resp.id })
              notify(this, 'success', `Batch ${resp.name ? resp.name : resp.code} saved`, 0)
            } else {
              this.createdNotif = this.$notify({
                title: 'Batch Saved',
                message: `Batch ${resp.name ? resp.name : resp.code} saved. Click to view.`,
                type: 'success',
                onClick: () => {
                  this.$emit('show', resp.id)
                  this.createdNotif.close()
                  this.createdNotif = null
                }
              })
            }
            const promises = await this.$store.dispatch('findDependencies', { batches: resp })
            await Promise.all(promises)
          } catch (e) {
            console.log(e)
            notify(this, 'error', e)
          }
        } else {
          let offset = 0                                                                        // Set offset so that notifs don't overlap each other
          validationErrors.messages.map(v => {                                                  // Render validation error notifs
            notify(this, 'warning', v, offset)
            offset += 100
          })
        }
      },
      /**
       * Edit an existing vendor
       * @param modelId   The ID of the vendor to be edited
       */
      editExistingModel (modelId) {
        this.clearForm()
        const model = this.getBatchById(modelId)
        for (let p in model) {
          if (!model.hasOwnProperty(p) || !Object.keys(this.form).includes(p)) continue
          if (p === 'production') {
            this.form[p] = {
              label: model.production ? this.getProductionById(model.production).name : '',
              value: model.production ? this.getProductionById(model.production).id : ''
            }
          } else if (p === 'company') {
            this.form[p] = {
              label: model.company ? this.getCompanyById(model.company).name : '',
              value: model.company ? this.getCompanyById(model.company).id : ''
            }
          } else if (p === 'default_series') {
            this.form[p] = {
              label: model.default_series ? this.getSeriesById(model.default_series).name : '',
              value: model.default_series ? this.getSeriesById(model.default_series).id : ''
            }
          } else if (p === 'default_location') {
            this.form[p] = {
              label: model.default_location ? this.getLocationById(model.default_location).name : '',
              value: model.default_location ? this.getLocationById(model.default_location).id : ''
            }
          } else if (p === 'default_set') {
            this.form[p] = {
              label: model.default_set ? this.getSetById(model.default_set).name : '',
              value: model.default_set ? this.getSetById(model.default_set).id : ''
            }
          } else if (p === 'default_free_field_1') {
            this.form[p] = {
              label: model.default_free_field_1 ? this.getFreefieldById(model.default_free_field_1).name : '',
              value: model.default_free_field_1 ? this.getFreefieldById(model.default_free_field_1).id : ''
            }
          } else if (p === 'default_free_field_2') {
            this.form[p] = {
              label: model.default_free_field_2 ? this.getFreefieldById(model.default_free_field_2).name : '',
              value: model.default_free_field_2 ? this.getFreefieldById(model.default_free_field_2).id : ''
            }
          } else if (p === 'default_tax_code') {
            this.form[p] = {
              label: model.default_tax_code ? this.getTaxcodeById(model.default_tax_code).name : '',
              value: model.default_tax_code ? this.getTaxcodeById(model.default_tax_code).id : ''
            }
          } else if (p === 'default_insurance') {
            this.form[p] = {
              label: model.default_insurance ? this.getInsuranceById(model.default_insurance).name : '',
              value: model.default_insurance ? this.getInsuranceById(model.default_insurance).id : ''
            }
          } else {
            this.form[p] = model[p]
          }
        }
        this.initialForm = JSON.stringify(this.form)
      },
      /**
       * Form change handler
       */
      formChanged () {
        this.trackFormChange()
      },
      /**
       * Resets/clears the form
       */
      clearForm () {
        this.form = Object.assign({}, this.formInitState)
        for (let field in this.form) {
          if (typeof this.form[field] === 'object') {
            this.form[field] = Object.assign({}, { label: '', value: '' })
          }
        }
        this.initialForm = JSON.stringify(this.form)
      },
      /**
       * Sets form default values
       */
      setDefaults () {
        const createLabelValueObj = (model) => {
          return { label: model && model.name ? model.name : '', value: model && model.id ? model.id : '' }
        }
        const { defaultBank, defaultProduction, defaultCompany } = this
        let tmpForm = Object.assign({}, this.form)
        tmpForm.bank = createLabelValueObj(defaultBank)
        tmpForm.production = createLabelValueObj(defaultProduction)
        tmpForm.company = createLabelValueObj(defaultCompany)
        this.form = Object.assign({}, tmpForm)
      }
    },
    computed: {
      ...mapGetters({
        saving: 'getBatchSaveStatus',
        updating: 'getBatchUpdateStatus',
        updateFailure: 'getBatchUpdateFailure',
        getCompanyById: 'getCompanyById',
        getProductionById: 'getProductionById',
        getSeriesById: 'getSeriesById',
        getLocationById: 'getLocationById',
        getSetById: 'getSetById',
        getFreefieldById: 'getFreefieldById',
        getTaxcodeById: 'getTaxcodeById',
        getInsuranceById: 'getInsuranceById',
        getCompanies: 'getAllCompanies',
        getProductions: 'getAllProductions',
        getSeries: 'getAllSeries',
        getLocations: 'getAllLocations',
        getSets: 'getAllSets',
        getFreefields: 'getAllFreefields',
        getTaxcodes: 'getAllTaxcodes',
        getInsurances: 'getAllInsurances',
        getBanks: 'getAllBanks',
        getBatchById: 'getBatchById',
        defaultBank: 'getDefaultBank',
        defaultCompany: 'getDefaultCompany',
        defaultProduction: 'getDefaultProduction',
        loadingData: 'getInitDataLoadingStatus'
      }),
      visible () {
        const visible = this.$store.getters.getModalVisibilityState
        if (visible.batch && typeof visible.batch === 'object' && visible.batch.visible) {
          return visible.batch.visible
        }
        return false
      },
      /**
       * Returns a boolean indicating whether or not the user is in a data entry view. This is necessary because
       * this.submit will need to emit a 'populateFieldsWithNewModel' event if the user is in a data entry view,
       * because the header field display values (such as production, company and bank need to be shown).
       */
      inDataEntryView () {
        const visible = this.$store.getters.getModalVisibilityState
        if (visible.batch && typeof visible.batch === 'object' && visible.batch.inDataEntryView) {
          return visible.batch.inDataEntryView
        }
        return false
      }
    },
    watch: {
      /**
       * If the modal becomes visible, clears the form
       */
      visible () {
        let dataLoader = null
        if (this.visible) {
          this.clearForm()
          this.setDefaults()
          dataLoader = new DataLoader(this.$store, [ bankActionPayload ])
          dataLoader.loadData()
        }
      },
      /**
       * Checks whether edit mode is active
       */
      editMode () {
        const { active, modelId } = this.editMode
        if (active && modelId) {
          this.editExistingModel(modelId)
        }
      },
      getCompanies () {
        this.nonAsyncSuggestions.company = this.getCompanies.map(v => {
          return { label: v.name, value: v.id }
        })
      },
      getProductions () {
        this.nonAsyncSuggestions.production = this.getProductions.map(v => {
          return { label: v.name, value: v.id }
        })
      },
      getBanks () {
        this.nonAsyncSuggestions.bank = this.getBanks.map(v => {
          return { label: v.code + ' | ' + v.name, value: v.id }
        })
      },
      getSeries () {
        this.nonAsyncSuggestions.default_series = this.getSeries.map(v => {
          return { label: v.name, value: v.id }
        })
      },
      getLocations () {
        this.nonAsyncSuggestions.default_location = this.getLocations.map(v => {
          return { label: v.name, value: v.id }
        })
      },
      getSets () {
        this.nonAsyncSuggestions.default_set = this.getSets.map(v => {
          return { label: v.name, value: v.id }
        })
      },
      getFreefields () {
        this.nonAsyncSuggestions.default_free_field_1 = this.nonAsyncSuggestions.default_free_field_2 = this.getFreefields.map(v => {
          return { label: v.name, value: v.id }
        })
      },
      getTaxcodes () {
        this.nonAsyncSuggestions.default_tax_code = this.getTaxcodes.map(v => {
          return { label: v.name, value: v.id }
        })
      },
      getInsurances () {
        this.nonAsyncSuggestions.default_insurance = this.getInsurances.map(v => {
          return { label: v.name, value: v.id }
        })
      }
    },
    created () {
      // Create a blank copy of the form on component creation
      this.formInitState = Object.assign({}, this.form)
      // Initializes production and company select boxes with options
      this.nonAsyncSuggestions.company = this.getCompanies.map(v => {
        return { label: v.name, value: v.id }
      })
      this.nonAsyncSuggestions.production = this.getProductions.map(v => {
        return { label: v.name, value: v.id }
      })
      this.nonAsyncSuggestions.bank = this.getBanks.map(v => {
        return { label: v.name, value: v.id }
      })
      this.nonAsyncSuggestions.default_series = this.getSeries.map(v => {
        return { label: v.name, value: v.id }
      })
      this.nonAsyncSuggestions.default_location = this.getLocations.map(v => {
        return { label: v.name, value: v.id }
      })
      this.nonAsyncSuggestions.default_set = this.getSets.map(v => {
        return { label: v.name, value: v.id }
      })
      this.nonAsyncSuggestions.default_free_field_1 = this.nonAsyncSuggestions.default_free_field_2 = this.getFreefields.map(v => {
        return { label: v.name, value: v.id }
      })
      this.nonAsyncSuggestions.default_tax_code = this.getTaxcodes.map(v => {
        return { label: v.name, value: v.id }
      })
      this.nonAsyncSuggestions.default_insurance = this.getInsurances.map(v => {
        return { label: v.name, value: v.id }
      })
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/RouteComponents/Batches/NewBatch.vue